import { helper as $helper }    from '@/utils/helper';
import { createLabel }          from '@/fabric/objects.js';

import {
    canvas,
    layout,
    currentBackground,
    scrollToElement,
    removeElement,
    clearCanvasEvents,
    disableCanvasActions,
    toggleCrosshairLines
} from '@/fabric/global.js';

let isEdgeScaling = false;
let scalingEdge = null;
let startPointer = null;
let startDimensions = null;
let scalingObject = null;
const edgeTolerance = 25;
const cornerSize = 40;

export function setupDrawBB () {
    clearCanvasEvents();
    disableCanvasActions();

    canvas.defaultCursor = "crosshair";
    canvas.getObjects().forEach(obj => { obj.set({ hoverCursor: "crosshair" })});

    let bbRect, origX, origY;

    canvas.on('mouse:down', function(e) {
        if (!e.target) { 
            if (!currentBackground.value) return;
            const { top: bgTop, left: bgLeft, height: bgHeight, width: bgWidth } = currentBackground.value;
            
            canvas._historyRecording    = false;
            layout.value.selectedBB     = false;
            layout.value.canvas.isDown  = true;

            const pointer = canvas.getPointer(e.e);
            
            origX = Math.max(bgLeft, Math.min(bgLeft + bgWidth, pointer.x));
            origY = Math.max(bgTop, Math.min(bgTop + bgHeight, pointer.y));

            const selectedTag   = layout.value.status.currentTag;
            const tagId         = selectedTag.id || '0';
            const tagName       = selectedTag.name || 'new';
            const color         = selectedTag.color || '#ff0000';
            const opacity       = layout.value.canvas.filters.opacity || 0.5;

            const labelOpt = {
                id:         tagId,
                name:       tagName,
                color:      color,
                fill:       $helper.hexToRgb(color, opacity),
                height:     0,
                width:      0,
                top:        origY,
                left:       origX
            }

            bbRect = createLabel(labelOpt);
            canvas.add(bbRect);
        }
    });

    canvas.on('mouse:move', (e) => {
        const pointer = canvas.getPointer(e.e);
        toggleCrosshairLines(true, pointer);

        if (!layout.value.canvas.isDown || !bbRect || !currentBackground.value) return;
        const { top: bgTop, left: bgLeft, height: bgHeight, width: bgWidth } = currentBackground.value;

        const clampedX = Math.max(bgLeft, Math.min(bgLeft + bgWidth, pointer.x));
        const clampedY = Math.max(bgTop, Math.min(bgTop + bgHeight, pointer.y));

        const [newLeft, newWidth]   = clampedX < origX ? [clampedX, origX - clampedX] : [origX, clampedX - origX];
        const [newTop, newHeight]   = clampedY < origY ? [clampedY, origY - clampedY] : [origY, clampedY - origY];

        bbRect.set({ left: newLeft, top: newTop, width: newWidth, height: newHeight });

        canvas.renderAll();
    });

    canvas.on('mouse:up', function(e) { 
        if (layout.value.canvas.isDown) {
            layout.value.canvas.isDown = false;

            if (bbRect.width === 0 && bbRect.height === 0) { 
                removeElement(bbRect.uuid);
            } else {
                layout.value._tempDrawingObject = bbRect;
                layout.value.selectedBB = bbRect.uuid;
    
                canvas.setActiveObject(bbRect);
                scrollToElement(bbRect.uuid);
                
                const confirmObject = (event) => {
                    if (event.key === "Escape") {
                        if (layout.value._tempDrawingObject) {
                            removeElement(layout.value._tempDrawingObject.uuid);
                            layout.value._tempDrawingObject = null;
                            canvas.renderAll();
                        }
                    } else {
                        if (layout.value._tempDrawingObject) {
                            layout.value._tempDrawingObject.isTemporary = false;
                            layout.value._tempDrawingObject = null;
    
                            canvas._historyRecording = true;
                            canvas._pushHistory();
    
                            canvas.renderAll();
                        }
                    }
                    
                    window.removeEventListener('keydown', confirmObject);
                    window.removeEventListener('mousedown', confirmObject);
                };
                
                window.addEventListener('keydown', confirmObject);
                window.addEventListener('mousedown', confirmObject);
            }
        }
        canvas.renderAll();
    });

    canvas.on('mouse:out', function() { 
        toggleCrosshairLines(canvas, false);
    });
}

export function setupMoveBB () {
    clearCanvasEvents();
    toggleCrosshairLines();

    canvas.defaultCursor = "move";
    canvas.getObjects().forEach(obj => { obj.set({ hoverCursor: "grab" })});

    canvas.findTarget = (function(originalFn) {
        return function(e, skipGroup) {
            let target = originalFn.call(this, e, skipGroup);
            
            if (target && target.elementType === 'bbRect') {
                const pointer = this.getPointer(e, true);
                const objects = this.getObjects().filter(obj => 
                    obj.elementType === 'bbRect' && 
                    obj.containsPoint(pointer)
                );

                if (objects.length > 1) {
                    objects.sort((a, b) => {
                        const areaA = a.width * a.height;
                        const areaB = b.width * b.height;
                        return areaA - areaB;
                    });
                    
                    target = objects[0];
                }
            }
            
            return target;
        };
    })(canvas.findTarget);

    canvas.getObjects().filter(obj => obj.elementType === 'bbRect').forEach(box => {
        box.set({ 
            selectable:         true,
            evented:            true,
            hasControls:        true,
            centeredScaling:    false,
            hoverCursor:        "move"
        });
        box.setCoords();
    });

    canvas.on("mouse:down", function(e) {
        if (e.target && e.target.elementType === 'bbRect') {
            const activeObj = canvas.getActiveObject();
            if (activeObj === e.target) {
                const pointer = canvas.getPointer(e.e);
                const edge = detectEdge(e.target, pointer);
                
                if (edge) {
                    startEdgeScaling(e.target, edge, pointer, e);
                    return;
                }
            }
            
            layout.value.selectedBB = e.target.uuid;
            canvas.setActiveObject(e.target);
            canvas.renderAll();
            scrollToElement(e.target.uuid);
        } else { 
            layout.value.selectedBB = false; 
            canvas.discardActiveObject();
            canvas.renderAll();
        }
    });

    canvas.on('object:moving', function(e) {
        handleDetectionLimits(e.target);
    });

    canvas.on('object:scaling', function(e) {
        handleDetectionLimits(e.target);
    });

    canvas.on('mouse:move', function(e) {
        if (isEdgeScaling) {
            handleEdgeScaling(e);
        } else {
            const activeObj = canvas.getActiveObject();
            if (activeObj && activeObj.elementType === 'bbRect' && !canvas._isCurrentlyDrawing) {
                const pointer = canvas.getPointer(e.e);
                
                if (activeObj.__corner) {
                    return;
                }
                
                if (canvas._currentTransform) {
                    return;
                }
                
                const edge = detectEdge(activeObj, pointer);
                if (edge) {
                    updateCursor(edge);
                } else {
                    const isInActiveBoxArea = isPointerInBoxArea(activeObj, pointer);
                    if (isInActiveBoxArea) {
                        canvas.defaultCursor = 'move';
                        canvas.setCursor('move');
                    }
                }
            } else {
                if (!canvas._currentTransform && !isEdgeScaling) {
                    canvas.defaultCursor = "move";
                    canvas.setCursor('move');
                }
            }
        }
    });

    canvas.on('mouse:up', function(e) {
        if (isEdgeScaling) {
            finishEdgeScaling();
        }
    });

    canvas.on('selection:cleared', function() {
        canvas.defaultCursor = "move";
    });

    canvas.on('selection:created', function() {
        canvas.defaultCursor = "move";
    });
}

function detectEdge(object, pointer) {
    if (!object || object.elementType !== 'bbRect') return null;
    
    const objectLeft = object.left;
    const objectTop = object.top;
    const objectWidth = object.width * object.scaleX;
    const objectHeight = object.height * object.scaleY;
    const objectRight = objectLeft + objectWidth;
    const objectBottom = objectTop + objectHeight;
    
    const tolerance = edgeTolerance;
    const corner = cornerSize;
    
    const isInTopLeftCorner = pointer.x >= objectLeft - corner && pointer.x <= objectLeft + corner && 
                              pointer.y >= objectTop - corner && pointer.y <= objectTop + corner;
    const isInTopRightCorner = pointer.x >= objectRight - corner && pointer.x <= objectRight + corner && 
                               pointer.y >= objectTop - corner && pointer.y <= objectTop + corner;
    const isInBottomLeftCorner = pointer.x >= objectLeft - corner && pointer.x <= objectLeft + corner && 
                                 pointer.y >= objectBottom - corner && pointer.y <= objectBottom + corner;
    const isInBottomRightCorner = pointer.x >= objectRight - corner && pointer.x <= objectRight + corner && 
                                  pointer.y >= objectBottom - corner && pointer.y <= objectBottom + corner;
    
    if (isInTopLeftCorner || isInTopRightCorner || isInBottomLeftCorner || isInBottomRightCorner) {
        return null;
    }
    
    const nearLeft = pointer.x >= objectLeft - tolerance && pointer.x <= objectLeft + tolerance;
    const nearRight = pointer.x >= objectRight - tolerance && pointer.x <= objectRight + tolerance;
    const nearTop = pointer.y >= objectTop - tolerance && pointer.y <= objectTop + tolerance;
    const nearBottom = pointer.y >= objectBottom - tolerance && pointer.y <= objectBottom + tolerance;
    
    const withinVertical = pointer.y >= objectTop + corner && pointer.y <= objectBottom - corner;
    const withinHorizontal = pointer.x >= objectLeft + corner && pointer.x <= objectRight - corner;
    
    if (nearLeft && withinVertical) {
        return 'left';
    }
    
    if (nearRight && withinVertical) {
        return 'right';
    }
    
    if (nearTop && withinHorizontal) {
        return 'top';
    }
    
    if (nearBottom && withinHorizontal) {
        return 'bottom';
    }
    
    return null;
}

function updateCursor(edge) {
    if (edge) {
        switch (edge) {
            case 'top':
            case 'bottom':
                canvas.defaultCursor = 'ns-resize';
                canvas.setCursor('ns-resize');
                break;
            case 'left':
            case 'right':
                canvas.defaultCursor = 'ew-resize';
                canvas.setCursor('ew-resize');
                break;
        }
    } else {
        canvas.defaultCursor = 'move';
        canvas.setCursor('move');
    }
}

function startEdgeScaling(object, edge, pointer, e) {
    isEdgeScaling = true;
    scalingEdge = edge;
    scalingObject = object;
    startPointer = pointer;
    
    startDimensions = {
        left: object.left,
        top: object.top,
        width: object.width,
        height: object.height,
        scaleX: object.scaleX,
        scaleY: object.scaleY
    };
    
    object.set({
        lockMovementX: true,
        lockMovementY: true
    });
    
    updateCursor(edge);
    
    e.e.stopPropagation();
    e.e.preventDefault();
}

function handleEdgeScaling(e) {
    if (!scalingObject || !scalingEdge || !startPointer) return;
    
    const pointer = canvas.getPointer(e.e);
    const deltaX = pointer.x - startPointer.x;
    const deltaY = pointer.y - startPointer.y;
    
    let newLeft = startDimensions.left;
    let newTop = startDimensions.top;
    let newScaleX = startDimensions.scaleX;
    let newScaleY = startDimensions.scaleY;
    
    switch (scalingEdge) {
        case 'top': {
            const newHeight = startDimensions.height * startDimensions.scaleY - deltaY;
            if (newHeight > 5) {
                newScaleY = newHeight / startDimensions.height;
                newTop = startDimensions.top + deltaY;
            }
            break;
        }
        case 'bottom': {
            const newHeight = startDimensions.height * startDimensions.scaleY + deltaY;
            if (newHeight > 5) {
                newScaleY = newHeight / startDimensions.height;
            }
            break;
        }
        case 'left': {
            const newWidth = startDimensions.width * startDimensions.scaleX - deltaX;
            if (newWidth > 5) {
                newScaleX = newWidth / startDimensions.width;
                newLeft = startDimensions.left + deltaX;
            }
            break;
        }
        case 'right': {
            const newWidth = startDimensions.width * startDimensions.scaleX + deltaX;
            if (newWidth > 5) {
                newScaleX = newWidth / startDimensions.width;
            }
            break;
        }
    }
    
    scalingObject.set({
        left: newLeft,
        top: newTop,
        scaleX: newScaleX,
        scaleY: newScaleY
    });
    
    handleDetectionLimits(scalingObject);
    scalingObject.setCoords();
    canvas.renderAll();
}

function finishEdgeScaling() {
    if (scalingObject) {
        scalingObject.set({
            lockMovementX: false,
            lockMovementY: false
        });
        scalingObject.setCoords();
        scalingObject.fire('modified');
    }
    
    isEdgeScaling = false;
    scalingEdge = null;
    startPointer = null;
    startDimensions = null;
    scalingObject = null;
    canvas.defaultCursor = "move";
    canvas.setCursor('move');
    canvas.renderAll();
}

function handleDetectionLimits (obj) {
    if (!obj || obj.type === 'activeSelection' || obj.elementType === 'background' || !currentBackground.value) return;
    
    const { top: bgTop, left: bgLeft, height: bgHeight, width: bgWidth } = currentBackground.value;
    const bgBottom = bgTop + bgHeight;
    const bgRight = bgLeft + bgWidth;
    
    const objWidth = obj.width * obj.scaleX;
    const objHeight = obj.height * obj.scaleY;
    
    let newLeft = obj.left;
    let newTop = obj.top;
    let newScaleX = obj.scaleX;
    let newScaleY = obj.scaleY;
    
    if (newLeft < bgLeft) {
        newLeft = bgLeft;
    }
    if (newTop < bgTop) {
        newTop = bgTop;
    }
    if (newLeft + objWidth > bgRight) {
        if (newLeft > bgLeft) {
            newLeft = bgRight - objWidth;
        } else {
            newScaleX = (bgRight - newLeft) / obj.width;
        }
    }
    if (newTop + objHeight > bgBottom) {
        if (newTop > bgTop) {
            newTop = bgBottom - objHeight;
        } else {
            newScaleY = (bgBottom - newTop) / obj.height;
        }
    }
    
    obj.set({
        left: Math.max(bgLeft, newLeft),
        top: Math.max(bgTop, newTop),
        scaleX: Math.max(0.1, newScaleX),
        scaleY: Math.max(0.1, newScaleY)
    });
    
    obj.setCoords();
}

function isPointerInBoxArea(box, pointer) {
    const tolerance = 30;
    const boxLeft = box.left - tolerance;
    const boxTop = box.top - tolerance;
    const boxWidth = (box.width * box.scaleX) + (tolerance * 2);
    const boxHeight = (box.height * box.scaleY) + (tolerance * 2);
    const boxRight = boxLeft + boxWidth;
    const boxBottom = boxTop + boxHeight;
    
    return pointer.x >= boxLeft && pointer.x <= boxRight && 
           pointer.y >= boxTop && pointer.y <= boxBottom;
}